import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ActionIcon } from '@mantine/core';
import { IconCamera, IconMinus, IconPlus } from '@tabler/icons-react';
import { ScanLine } from './scanTypes';
import classes from './ScanStage.module.css';

const MAX_ZOOM = 6;
// Taps up to this far outside a line's box still count, so small vertical names are easy to hit
const TAP_SLOP_PX = 14;

interface ScanStageProps {
  photoUrl: string | null;
  lines: ScanLine[];
  selected: number;
  scanning: boolean;
  onTapLine: (index: number) => void;
  onTapEmpty: () => void;
  onRetake: () => void;
  /** Shown over the bottom of the photo (a hint or status); taps pass through it */
  overlay?: React.ReactNode;
  /** Fill most of the screen, for pages with nothing below the photo */
  tall?: boolean;
  /** Viewport px covered at the top by something floating over the stage; `reveal` keeps lines below it */
  topInset?: number;
}

export interface ScanStageHandle {
  /**
   * Bring a line into view, zooming in if it's small. With `sheetTop` (viewport px; a sheet covers
   * everything below it) the line is then moved into the space above the sheet: the photo slides up
   * as far as the sheet covers it, and the page scrolls for whatever that can't do.
   */
  reveal: (index: number, zoomIn: boolean, sheetTop?: () => number) => void;
  /** The sheet has closed: slide the photo back down if `reveal` lifted it. */
  release: () => void;
}

interface Box { x0: number; x1: number; y0: number; y1: number }

const points = (q: number[]) => [0, 2, 4, 6].map((k) => `${q[k]},${q[k + 1]}`).join(' ');

/**
 * The label box's geometry in fit-pixel space (w,h = the photo's unzoomed size
 * on screen). A name longer than its line grows the box on past it (toward the
 * photo's edge) rather than shrinking to nothing. Shared by the drawing and by
 * hit-testing, so a tap anywhere on the (possibly wider) label box counts as a
 * tap on its line, not on whatever's underneath it.
 */
function labelGeometry(line: ScanLine, w: number, h: number) {
  const xs = [line.q[0], line.q[2], line.q[4], line.q[6]].map((u) => u * w);
  const ys = [line.q[1], line.q[3], line.q[5], line.q[7]].map((v) => v * h);
  const x0 = Math.min(...xs), x1 = Math.max(...xs), y0 = Math.min(...ys), y1 = Math.max(...ys);
  const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
  const vertical = line.v === 1 || y1 - y0 > (x1 - x0) * 1.2;
  const bw = vertical ? y1 - y0 : x1 - x0, bh = vertical ? x1 - x0 : y1 - y0;
  // The sheet shows the full name; on the photo, "Harami (Beef Skirt Steak)" is "Harami"
  const text = (line.label || '').replace(/\s*[(（].*$/, '').trim() || line.label || '';
  const em = Math.max(text.length, 1) * 0.55 + 0.5;
  const avail = Math.max(bw, (vertical ? h - y0 : w - x0));
  let fontSize = bh * 0.74;
  let width = Math.max(bw, fontSize * em);
  if (width > avail) { width = avail; fontSize = avail / em; }
  const left = cx - bw / 2;
  return { cx, cy, bw, bh, width, fontSize, vertical, left, text };
}

/** The label's bounding box in fit-pixel space, rotated (if vertical) the same quarter turn as its `<g>`. */
function labelBounds(line: ScanLine, w: number, h: number): Box {
  const { cx, cy, bh, width, vertical, left } = labelGeometry(line, w, h);
  const rx0 = left, rx1 = left + width, ry0 = cy - bh / 2, ry1 = cy + bh / 2;
  if (!vertical) return { x0: rx0, x1: rx1, y0: ry0, y1: ry1 };
  // 90° clockwise about (cx, cy), matching the SVG `rotate(90 cx cy)` transform
  const corners = [[rx0, ry0], [rx1, ry0], [rx0, ry1], [rx1, ry1]].map(([px, py]) => [cx - (py - cy), cy + (px - cx)]);
  const cxs = corners.map((c) => c[0]), cys = corners.map((c) => c[1]);
  return { x0: Math.min(...cxs), x1: Math.max(...cxs), y0: Math.min(...cys), y1: Math.max(...cys) };
}

/**
 * An opaque box over a line with its English title. Vertical lines are turned
 * a quarter turn, so the title runs down the box.
 */
const LabelBox: React.FC<{ line: ScanLine; w: number; h: number; selected: boolean; delay: number }> = ({ line, w, h, selected, delay }) => {
  const { cx, cy, bh, width, fontSize, vertical, left, text } = labelGeometry(line, w, h);
  return (
    <g transform={vertical ? `rotate(90 ${cx} ${cy})` : undefined}>
      {/* Plays once, when the title first appears */}
      <g className={classes.labelIn} style={{ animationDelay: `${delay}ms` }}>
        <rect x={left} y={cy - bh / 2} width={width} height={bh} rx={Math.min(3, bh / 6)} className={selected ? classes.labelBoxSelected : classes.labelBox} />
        <text x={left + width / 2} y={cy} fontSize={fontSize} textAnchor="middle" dominantBaseline="central" className={classes.labelText} style={{ animationDelay: `${delay + 160}ms` }}>{text}</text>
      </g>
      <rect x={left} y={cy - bh / 2} width={width} height={bh} rx={Math.min(3, bh / 6)} className={classes.labelPing} style={{ animationDelay: `${delay}ms` }} />
    </g>
  );
};

/**
 * The menu photo with every line of text outlined. Lines we know are marked
 * orange (green when their English is ready but hidden until tapped); lines judged not to be menu items are grey. Pinch to zoom (up to 6×), drag to pan when zoomed, double-tap to
 * zoom in or out. At 1× a vertical swipe scrolls the page as usual.
 */
export const ScanStage = forwardRef<ScanStageHandle, ScanStageProps>(function ScanStage(
  { photoUrl, lines, selected, scanning, onTapLine, onTapEmpty, onRetake, overlay, tall, topInset = 0 },
  ref
) {
  const stageRef = useRef<HTMLDivElement>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  // The view lives in a ref and goes straight to the DOM: a pinch shouldn't re-render React
  // lift: how far the photo may slide up past its usual limits, while a sheet covers the stage's bottom
  const view = useRef({ s: 1, x: 0, y: 0, fw: 0, fh: 0, lift: 0 });
  const [zoomed, setZoomed] = useState(false);
  // The photo's size on screen at 1×, so English titles can be drawn in pixels
  const [size, setSize] = useState({ w: 0, h: 0 });

  const latest = useRef({ lines, onTapLine, onTapEmpty, topInset });
  latest.current = { lines, onTapLine, onTapEmpty, topInset };

  const setView = useCallback((s: number, x: number, y: number, glide = false) => {
    const stage = stageRef.current, layer = layerRef.current;
    if (!stage || !layer) return;
    const V = view.current;
    V.s = Math.min(MAX_ZOOM, Math.max(1, s));
    const W = stage.clientWidth, H = stage.clientHeight, w = V.fw * V.s, h = V.fh * V.s;
    V.x = w <= W ? (W - w) / 2 : Math.min(0, Math.max(W - w, x));
    const yHi = h <= H ? (H - h) / 2 : 0;
    V.y = Math.min(yHi, Math.max((h <= H ? yHi : H - h) - V.lift, y));
    layer.classList.toggle(classes.glide, glide);
    layer.style.transform = `translate(${V.x}px, ${V.y}px) scale(${V.s})`;
    layer.style.setProperty('--z', String(V.s));
    const isZoomed = V.s > 1.01;
    stage.classList.toggle(classes.zoomed, isZoomed);
    setZoomed((z) => (z === isZoomed ? z : isZoomed));
  }, []);

  const zoomAround = useCallback((s: number, px: number, py: number, glide = false) => {
    const V = view.current;
    const ix = (px - V.x) / V.s, iy = (py - V.y) / V.s;
    const ns = Math.min(MAX_ZOOM, Math.max(1, s));
    setView(ns, px - ix * ns, py - iy * ns, glide);
  }, [setView]);

  const fit = useCallback((keepZoom = false) => {
    const stage = stageRef.current, img = imgRef.current, layer = layerRef.current;
    if (!stage || !img || !layer || !img.naturalWidth) return;
    const V = view.current;
    const { s, x, y } = V;
    const a = img.naturalWidth / img.naturalHeight;
    V.fw = Math.min(stage.clientWidth, stage.clientHeight * a);
    V.fh = V.fw / a;
    layer.style.width = `${V.fw}px`;
    layer.style.height = `${V.fh}px`;
    setSize((z) => (z.w === V.fw && z.h === V.fh ? z : { w: V.fw, h: V.fh }));
    if (keepZoom && s > 1) setView(s, x, y);
    // At 1× the photo is centred, or lifted above an open sheet: keep any lift through a resize
    else setView(1, 0, y);
  }, [setView]);

  // A line's box on screen, in stage pixels
  const screenBox = useCallback((l: ScanLine): Box => {
    const V = view.current;
    const xs = [l.q[0], l.q[2], l.q[4], l.q[6]].map((u) => V.x + u * V.fw * V.s);
    const ys = [l.q[1], l.q[3], l.q[5], l.q[7]].map((v) => V.y + v * V.fh * V.s);
    return { x0: Math.min(...xs), x1: Math.max(...xs), y0: Math.min(...ys), y1: Math.max(...ys) };
  }, []);

  // A line's label box on screen (if it has one), in stage pixels — can be wider than its OCR box
  const labelScreenBox = useCallback((l: ScanLine): Box | null => {
    if (!l.label || l.dropped || size.w <= 0) return null;
    const V = view.current;
    const b = labelBounds(l, size.w, size.h);
    return { x0: V.x + b.x0 * V.s, x1: V.x + b.x1 * V.s, y0: V.y + b.y0 * V.s, y1: V.y + b.y1 * V.s };
  }, [size.w, size.h]);

  // Which line is under a tap? The nearest within the slop (its OCR box, or its wider label box); known dishes win close calls, then the smaller box.
  const hitTest = useCallback((px: number, py: number) => {
    let best = -1, bestScore = Infinity, bestArea = Infinity;
    latest.current.lines.forEach((l, i) => {
      if (l.dropped) return;
      const b = screenBox(l);
      const lb = labelScreenBox(l);
      const box = lb ? { x0: Math.min(b.x0, lb.x0), x1: Math.max(b.x1, lb.x1), y0: Math.min(b.y0, lb.y0), y1: Math.max(b.y1, lb.y1) } : b;
      const d = Math.hypot(Math.max(box.x0 - px, 0, px - box.x1), Math.max(box.y0 - py, 0, py - box.y1));
      if (d > TAP_SLOP_PX) return;
      const score = d + (l.match || l.hit ? 0 : 4);
      const area = (box.x1 - box.x0) * (box.y1 - box.y0);
      if (score < bestScore - 0.5 || (Math.abs(score - bestScore) <= 0.5 && area < bestArea)) {
        best = i; bestScore = score; bestArea = area;
      }
    });
    return best;
  }, [screenBox, labelScreenBox]);

  useImperativeHandle(ref, () => ({
    reveal(i, zoomIn, sheetTop) {
      requestAnimationFrame(() => {
        const stage = stageRef.current, line = latest.current.lines[i];
        if (!stage || !line) return;
        const V = view.current;
        const W = stage.clientWidth, H = stage.clientHeight;
        const b = screenBox(line);
        if (zoomIn && b.x1 - b.x0 < W * 0.3 && b.y1 - b.y0 < H * 0.3) {
          const want = Math.min(3, Math.max(V.s, Math.min((W * 0.5) / ((b.x1 - b.x0) / V.s), (H * 0.35) / ((b.y1 - b.y0) / V.s))));
          const cx = ((b.x0 + b.x1) / 2 - V.x) / V.s, cy = ((b.y0 + b.y1) / 2 - V.y) / V.s;
          setView(want, W / 2 - cx * want, H * 0.4 - cy * want, true);
        } else if (V.s > 1.01 && (b.x0 < 0 || b.x1 > W || b.y0 < 0 || b.y1 > H)) {
          setView(V.s, V.x + W / 2 - (b.x0 + b.x1) / 2, V.y + H * 0.4 - (b.y0 + b.y1) / 2, true);
        }
        // Then move the line into the space above the sheet: slide the photo up (as far as the
        // sheet covers it), and scroll the page for the rest. V is already where any glide ends.
        if (!sheetTop) return;
        const r = stage.getBoundingClientRect(), limit = sheetTop();
        V.lift = Math.max(0, r.bottom - limit);
        const bb = screenBox(line);
        const lineTop = r.top + bb.y0, lineBottom = r.top + bb.y1;
        const top = Math.max(r.top, 0, latest.current.topInset || 0) + 8, bottom = limit - 8;
        if (lineTop >= top && lineBottom <= bottom) return;
        const want = (top + bottom) / 2 - (lineTop + lineBottom) / 2;
        const y0 = V.y;
        setView(V.s, V.x, V.y + want, true);
        const rest = want - (V.y - y0);
        if (Math.abs(rest) > 8) window.scrollBy({ top: -rest, behavior: 'smooth' });
      });
    },
    release() {
      const V = view.current;
      if (!V.lift) return;
      V.lift = 0;
      setView(V.s, V.x, V.y, true);
    },
  }), [screenBox, setView]);

  // Pointer gestures: tap, double-tap, pan and pinch
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const ptrs = new Map<number, { x: number; y: number }>();
    type Gesture =
      | { kind: 'tap' | 'pan'; x0: number; y0: number; t0: number; vx: number; vy: number; moved: number }
      | { kind: 'pinch'; d0: number; s0: number; ix: number; iy: number };
    let gesture: Gesture | null = null;
    let lastTap: { t: number; x: number; y: number } | null = null;

    const down = (e: PointerEvent) => {
      if ((e.target as Element).closest('button')) return;
      try { stage.setPointerCapture(e.pointerId); } catch (err) { /* not capturable */ }
      ptrs.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const V = view.current;
      if (ptrs.size === 1) gesture = { kind: 'tap', x0: e.clientX, y0: e.clientY, t0: performance.now(), vx: V.x, vy: V.y, moved: 0 };
      if (ptrs.size === 2) {
        const r = stage.getBoundingClientRect();
        const [a, b] = Array.from(ptrs.values());
        const mx = (a.x + b.x) / 2 - r.left, my = (a.y + b.y) / 2 - r.top;
        gesture = { kind: 'pinch', d0: Math.hypot(a.x - b.x, a.y - b.y) || 1, s0: V.s, ix: (mx - V.x) / V.s, iy: (my - V.y) / V.s };
      }
    };
    const move = (e: PointerEvent) => {
      if (!ptrs.has(e.pointerId) || !gesture) return;
      ptrs.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (gesture.kind === 'pinch' && ptrs.size >= 2) {
        const r = stage.getBoundingClientRect();
        const [a, b] = Array.from(ptrs.values());
        const s = Math.min(MAX_ZOOM, Math.max(1, gesture.s0 * Math.hypot(a.x - b.x, a.y - b.y) / gesture.d0));
        const mx = (a.x + b.x) / 2 - r.left, my = (a.y + b.y) / 2 - r.top;
        setView(s, mx - gesture.ix * s, my - gesture.iy * s);
      } else if (gesture.kind !== 'pinch') {
        const dx = e.clientX - gesture.x0, dy = e.clientY - gesture.y0;
        gesture.moved = Math.max(gesture.moved, Math.hypot(dx, dy));
        if (view.current.s > 1.01 && gesture.moved > 6) {
          gesture.kind = 'pan';
          setView(view.current.s, gesture.vx + dx, gesture.vy + dy);
        }
      }
    };
    const up = (e: PointerEvent) => {
      if (!ptrs.has(e.pointerId)) return;
      ptrs.delete(e.pointerId);
      if (e.type === 'pointercancel') { gesture = null; return; }
      if (gesture?.kind === 'pinch') { if (!ptrs.size) gesture = null; return; }
      if (gesture?.kind === 'tap' && gesture.moved < 10 && performance.now() - gesture.t0 < 500) {
        const r = stage.getBoundingClientRect(), px = e.clientX - r.left, py = e.clientY - r.top;
        const now = performance.now();
        if (lastTap && now - lastTap.t < 320 && Math.hypot(px - lastTap.x, py - lastTap.y) < 30) {
          zoomAround(view.current.s > 1.5 ? 1 : 2.5, px, py, true);
          lastTap = null;
        } else {
          lastTap = { t: now, x: px, y: py };
          const i = hitTest(px, py);
          if (i >= 0) latest.current.onTapLine(i);
          else latest.current.onTapEmpty();
        }
      }
      gesture = null;
    };
    const wheel = (e: WheelEvent) => {
      if (!e.ctrlKey && view.current.s <= 1.01) return; // let the page scroll
      e.preventDefault();
      const r = stage.getBoundingClientRect();
      if (e.ctrlKey) zoomAround(view.current.s * Math.exp(-e.deltaY / 200), e.clientX - r.left, e.clientY - r.top);
      else setView(view.current.s, view.current.x - e.deltaX, view.current.y - e.deltaY);
    };
    const resize = () => fit(true);

    stage.addEventListener('pointerdown', down);
    stage.addEventListener('pointermove', move);
    stage.addEventListener('pointerup', up);
    stage.addEventListener('pointercancel', up);
    stage.addEventListener('wheel', wheel, { passive: false });
    window.addEventListener('resize', resize);
    return () => {
      stage.removeEventListener('pointerdown', down);
      stage.removeEventListener('pointermove', move);
      stage.removeEventListener('pointerup', up);
      stage.removeEventListener('pointercancel', up);
      stage.removeEventListener('wheel', wheel);
      window.removeEventListener('resize', resize);
    };
  }, [fit, hitTest, setView, zoomAround]);

  const zoomBy = (factor: number) => {
    const stage = stageRef.current;
    if (!stage) return;
    const s = view.current.s * factor;
    zoomAround(factor < 1 && s < 1.2 ? 1 : s, stage.clientWidth / 2, stage.clientHeight / 2, true);
  };

  // Titles that appear together (the first scan) pop in one after another; one added by a tap pops in alone
  const shown = useRef(new Set<number>());
  const fresh = lines.map((l, i) => (l.label && !l.dropped && !shown.current.has(i) ? i : -1)).filter((i) => i >= 0);
  const labelDelay = (i: number) => (fresh.length > 1 ? Math.min(fresh.indexOf(i), 24) * 45 : 0);
  useEffect(() => {
    if (!lines.some((l) => l.label && !l.dropped)) shown.current.clear();
    lines.forEach((l, i) => { if (l.label && !l.dropped) shown.current.add(i); });
  });

  const sel = lines[selected];
  return (
    <div
      ref={stageRef}
      className={`${classes.stage} ${tall ? classes.tall : ''}`}
      aria-label="The menu photo. Every line of text is outlined; tap one to see what it is."
    >
      <div ref={layerRef} className={classes.layer}>
        {photoUrl && <img ref={imgRef} src={photoUrl} alt="" onLoad={() => fit()} />}
        <svg viewBox="0 0 1 1" preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <mask id="scan-holes" maskUnits="userSpaceOnUse" x="-1" y="-1" width="3" height="3">
              <rect x="-1" y="-1" width="3" height="3" fill="#fff" />
              {lines.map((l, i) => <polygon key={i} points={points(l.q)} fill="#000" />)}
            </mask>
          </defs>
          {lines.length > 0 && <rect className={classes.shade} x="0" y="0" width="1" height="1" mask="url(#scan-holes)" />}
          {lines.map((l, i) => i !== selected && (
            <g key={i}>
              <polygon className={l.dropped ? classes.dropped : l.ready ? classes.ready : l.match || l.hit ? classes.known : classes.text} points={points(l.q)} />
              <polygon className={l.dropped ? classes.droppedEdge : l.ready ? classes.readyEdge : l.match || l.hit ? classes.knownEdge : classes.textEdge} points={points(l.q)} />
            </g>
          ))}
          {/* The selected line last, on top of its neighbours */}
          {sel && (
            <g>
              <polygon className={classes.glow} points={points(sel.q)} />
              <polygon className={sel.match || sel.hit ? classes.knownSelected : classes.textSelected} points={points(sel.q)} />
              <polygon className={classes.selectedEdge} points={points(sel.q)} />
            </g>
          )}
        </svg>
        {size.w > 0 && lines.some((l) => l.label && !l.dropped) && (
          <svg viewBox={`0 0 ${size.w} ${size.h}`} aria-hidden="true">
            {lines.map((l, i) => l.label && !l.dropped && <LabelBox key={i} line={l} w={size.w} h={size.h} selected={i === selected} delay={labelDelay(i)} />)}
          </svg>
        )}
      </div>
      {scanning && <div className={classes.scanSweep} />}
      {overlay && <div className={classes.overlay}>{overlay}</div>}
      <ActionIcon
        className={classes.retake}
        size={44}
        radius="xl"
        variant="default"
        onClick={onRetake}
        aria-label="Photograph another menu"
      >
        <IconCamera size={22} />
      </ActionIcon>
      <div className={classes.zoomTools}>
        {zoomed && (
          <ActionIcon size={44} radius="xl" variant="default" onClick={() => zoomBy(1 / 1.8)} aria-label="Zoom out">
            <IconMinus size={20} />
          </ActionIcon>
        )}
        <ActionIcon size={44} radius="xl" variant="default" onClick={() => zoomBy(1.8)} aria-label="Zoom in">
          <IconPlus size={20} />
        </ActionIcon>
      </div>
    </div>
  );
});
